import { supabase } from '@/integrations/supabase/client';
import { RealtimeChannel, REALTIME_SUBSCRIBE_STATES } from '@supabase/supabase-js';

/**
 * Factory to create unique channel names and manage subscriptions with logging and retry logic.
 */
export const createRealtimeChannel = (
  channelName: string,
  options: {
    onStatusChange?: (status: REALTIME_SUBSCRIBE_STATES, error?: Error) => void;
    maxRetries?: number;
    initialBackoff?: number;
    maxBackoff?: number;
  } = {}
): RealtimeChannel => {
  const { 
    onStatusChange, 
    maxRetries = 10, 
    initialBackoff = 1000, 
    maxBackoff = 30000 
  } = options;
  
  let retryCount = 0;
  let timer: ReturnType<typeof setTimeout> | null = null;
  
  // Ensure unique channel name per instance
  const uniqueName = `${channelName}:${Math.random().toString(36).slice(2, 9)}:${Date.now()}`;
  
  const channel = supabase.channel(uniqueName);
  const originalSubscribe = channel.subscribe.bind(channel);

  console.log(`[Realtime] [${new Date().toISOString()}] Initializing channel: ${uniqueName}`);

  const subscribeWithRetry = () => {
    originalSubscribe((status: REALTIME_SUBSCRIBE_STATES, err?: Error) => {
      const timestamp = new Date().toISOString();
      console.log(`[Realtime] [${timestamp}] Channel ${uniqueName} status: ${status}`);

      if (status === 'SUBSCRIBED') {
        retryCount = 0;
        if (timer) {
          clearTimeout(timer);
          timer = null;
        }
      } else if (status === 'CHANNEL_ERROR') {
        if (retryCount < maxRetries) {
          const backoff = Math.min(maxBackoff, initialBackoff * Math.pow(2, retryCount));
          const jitter = Math.random() * 1000;
          const delay = backoff + jitter;

          console.warn(`[Realtime] [${timestamp}] Reconnecting ${uniqueName} in ${Math.round(delay)}ms (Attempt ${retryCount + 1}/${maxRetries}). Reason: ${err?.message || 'Unknown error'}`);

          retryCount++;
          if (timer) clearTimeout(timer);
          timer = setTimeout(() => {
            subscribeWithRetry();
          }, delay);
        } else {
          console.error(`[Realtime] [${timestamp}] Max retries reached for ${uniqueName}`);
        }
      }

      if (onStatusChange) onStatusChange(status, err);
    });
  };

  channel.subscribe = (() => {
    subscribeWithRetry();
    return channel;
  }) as typeof channel.subscribe;

  return channel;
};

/**
 * Helper to safely cleanup a channel
 */
export const cleanupChannel = async (channel: RealtimeChannel | null) => {
  if (!channel) return;
  
  const name = channel.topic;
  console.log(`[Realtime] Cleaning up channel: ${name} at ${new Date().toISOString()}`);
  
  try {
    await supabase.removeChannel(channel);
    console.log(`[Realtime] Channel ${name} removed successfully`);
  } catch (error) {
    console.error(`[Realtime] Error removing channel ${name}:`, error);
  }
};
